import GroupManagementInviteEntry from "./GroupManagementInviteEntry";
import GroupManagementHeader from "./GroupManagementHeader";
import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import G from './Getters';
import A from './ActionCreators';
import Id from './IdFactory.js';
//import "./_group_invite.css"; // ← import the CSS above

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function GroupInvite() {
  const dispatch = useDispatch();
  const savedGroups = useSelector(state => state.data.groups);

  const setGroups = () => {};
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [tempGroup, setTempGroup] = useState();

  const groups = {
    ...(tempGroup ? { tempGroup } : {}),
    ...savedGroups,
  };

  const userCanEdit = useSelector(state => {
    if (tempGroup) return true;
    return selectedGroupId && G.canUserSetPermissions(state, { type: "group", _id: selectedGroupId });
  });

  const selectedGroup = tempGroup || groups[selectedGroupId];
  const members = selectedGroup?.members || {};
  const invites = selectedGroup?.invites || {};
  const pending = Object.entries(invites).filter(([k]) => !members[k]);
  const title = selectedGroup?.name;

  const [addMode, setAddMode] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [toInvite, setToInvite] = useState([]);
  const [errors, setErrors] = useState([]);
  const [editingTitle, setEditingTitle] = useState(false);

  const nextId = useRef(1000);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (addMode && textareaRef.current) textareaRef.current.focus();
  }, [addMode]);

  function parseEmails(input) {
    if (!input) return [];
    return input.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
  }

  function validateEmail(e) {
    return EMAIL_RE.test(e);
  }

  function handlePasteChange(e) {
    const text = e.target.value;
    setPasteText(text);
    const raw = parseEmails(text);
    const valid = Array.from(new Set(raw.filter(validateEmail)));
    const invalid = raw.filter((x) => !validateEmail(x));
    setErrors(invalid);
    setToInvite(valid);
  }

  function handleAddToMembers() {
    if (toInvite.length === 0) {
      setErrors(["No valid emails to add."]);
      return;
    }
    const groupId = Id();

    if (tempGroup) {
      dispatch(A.createGroup({
        invites: Object.fromEntries(toInvite.map(email => ([email, "member"]))),
        name: tempGroup.name,
        _id: groupId,
        onSuccess: () => {
          setSelectedGroupId(groupId);
          setTempGroup();
        }
      }));
    }

    const existing = new Set(Object.keys(members));
    const newMembers = toInvite.filter((t) => !existing.has(t.toLowerCase()));

    if (newMembers.length === 0) {
      setErrors(["All emails already exist."]);
      return;
    }

    setToInvite([]);
    setPasteText("");
    setErrors([]);
    setAddMode(false);
  }

  function changeRole(id, role) {
    const updated = members.map?.((x) => (x._id === id ? { ...x, role } : x)) || [];
    setGroups((g) => g.map((group) => group._id === selectedGroupId ? { ...group, members: updated } : group));
  }

  function removeMember(id) {
    const updated = members.filter?.((x) => x._id !== id) || [];
    setGroups((g) => g.map((group) => group._id === selectedGroupId ? { ...group, members: updated } : group));
  }

  function cancelInvite(id) {
    const updated = members.filter?.((x) => !(x._id === id && x.status === "pending")) || [];
    setGroups((g) => g.map((group) => group._id === selectedGroupId ? { ...group, members: updated } : group));
  }

  function addNewGroup() {
    const newGroup = { name: `New Group ${Object.keys(groups).length + 1}`, members: {}, invites: {} };
    setTempGroup(newGroup);
    setSelectedGroupId("tempGroup");
  }

  function handleTitleChange(e) {
    setEditingTitle(e.target.value);
  }

  function saveTitle(e) {
    if (!userCanEdit) return;

    if (tempGroup) {
      setTempGroup(gr => ({ ...gr, name: e.target.value }));
    } else {
      dispatch(A.setItemProperties({
        itemType: "groups",
        _id: selectedGroupId,
        properties: { name: e.target.value }
      }));
      dispatch(A.syncChanges());
    }

    setEditingTitle(false);
  }

  const mainContent = (
    <div className="gi-main">
      {/* Sticky title + add invites controls */}
      <div className="gi-sticky">
        <GroupManagementHeader
          {...{
            title,
            editingTitle,
            setEditingTitle,
            saveTitle,
            handleTitleChange,
            userCanEdit,
          }}
        />

        {/* Add Mode button directly below title */}
        {userCanEdit && (
          <div className="gi-addbar">
            <button
              onClick={() => setAddMode(!addMode)}
              className="gi-btn gi-btn--primary"
            >
              {addMode ? "Cancel" : "+ Add Invites"}
            </button>
          </div>
        )}
      </div>

      {addMode && (
        <GroupManagementInviteEntry
          {...{
            textareaRef,
            pasteText,
            handlePasteChange,
            errors,
            toInvite,
            handleAddToMembers,
            setAddMode,
          }}
        />
      )}

      <section className="gi-grid">
        {/* Members (Accepted) */}
        <div className="gi-card">
          <h3 className="gi-card__title">Members (Accepted)</h3>

          {Object.values(members).length === 0 ? (
            <div className="gi-muted">No accepted members yet.</div>
          ) : (
            <div className="gi-rows">
              {Object.entries(selectedGroup.members).map(([id, m]) => (
                <div key={m._id || id} className="gi-row">
                  <div>{id}</div>
                  {userCanEdit && (
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <select
                        value={m.role}
                        onChange={(e) => changeRole(m._id, e.target.value)}
                        className="gi-select"
                      >
                        <option>Member</option>
                        <option>Admin</option>
                      </select>
                      <button
                        onClick={() => removeMember(m._id)}
                        className="gi-btn gi-btn--danger"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pending Invites */}
        <div className="gi-card">
          <h3 className="gi-card__title">Pending Invites</h3>

          {pending.length === 0 ? (
            <div className="gi-muted">No pending invites.</div>
          ) : (
            <div className="gi-rows">
              {pending.map((m) => {
                // m is [email, role] from Object.entries(invites)
                const email = m[0];
                let { role }  = m[1];
                role = role[0].toUpperCase() + role.slice(1);
                return (
                  <div key={email} className="gi-row">
                    <div>{email}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span className="gi-muted" style={{ fontSize: 14 }}>
                        {role}
                      </span>
                      {userCanEdit && (
                        <button
                          onClick={() => cancelInvite(email)}
                          className="gi-btn gi-btn--danger"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );

  return (
    <>
      <div className="gi-app">
        {/* Sidebar */}
        <aside className="gi-sidebar">
          <div className="gi-sidebar__header">
            <h2 className="gi-sidebar__title">Groups</h2>
            <button onClick={addNewGroup} className="gi-btn gi-btn--primary">New</button>
          </div>

          <div className="gi-group-list">
            {Object.entries(groups).map(([_id, g]) => (
              <div key={_id}>
                <button
                  onClick={() => setSelectedGroupId(_id)}
                  className={
                    "gi-group-list__item" +
                    (selectedGroupId === _id ? " gi-group-list__item--active" : "")
                  }
                >
                  <div>{g.name}</div>
                </button>
              </div>
            ))}
          </div>
        </aside>

        {/* Main Area */}
        {!selectedGroup && (
          <div className="gi-placeholder">
            <span>Select a group or</span>
            <button onClick={addNewGroup} className="gi-link">create a new one</button>
          </div>
        )}
        {!!selectedGroup && mainContent}
      </div>
    </>
  );
}
